import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useIntegrationApp } from '@membranehq/react';
import {
  getCampaignAgentIds,
  getCampaignContextParams,
} from 'src/services/campaigns/campaigns.service';
import { getReferencedContextParams } from 'src/interfaces/agent.interface';
import { Agent } from 'src/interfaces/agent.interface';
import useAuthedRequest, { AuthedRequest } from 'src/hooks/useAuthedRequest';
import {
  ContactRecord,
  listAction,
  DataSourceKey,
} from 'src/services/integration-app.service';
import { CrmField } from '../components/DraggableCrmField';
import { useCrmSync, CrmSyncStep } from './CrmSyncContext';
import { useCampaignContext } from './CampaignContext';
import { Option } from 'src/components/SelectBox';

// Field types for the mapping
interface CampaignField {
  name: string;
  label: string;
  required: boolean;
  description?: string;
}

interface FieldMappingEntry {
  campaignField: string;
  crmFieldValues: string[];
}

interface AddFieldToMappingOptions {
  index?: number;
}

const fetchAgentsById = async (
  agentIds: string[],
  authedRequest: AuthedRequest,
): Promise<Agent[]> => {
  return Promise.all(
    agentIds.map((agentId) => authedRequest.get(`/agents/${agentId}`)),
  );
};

// Types
interface FieldMappingContextValue {
  // State
  fieldMapping: Record<string, string>;
  isFieldMappingValid: boolean;
  availableCrmFields: CrmField[];
  campaignFields: CampaignField[];
  mappingEntries: FieldMappingEntry[];
  isInitialized: boolean;
  searchQuery: string;
  sampleContacts: ContactRecord[];

  // Setters
  setSearchQuery: (query: string) => void;

  // Actions
  addFieldToMapping: (
    campaignField: string,
    crmFieldValue: string,
    options?: AddFieldToMappingOptions,
  ) => void;
  removeFieldFromMapping: (
    campaignField: string,
    crmFieldValue: string,
  ) => void;
  reorderFieldsInMapping: (
    campaignField: string,
    fromIndex: number,
    toIndex: number,
  ) => void;
  clearMapping: (campaignField: string) => void;

  // Computed values
  mappedFieldValues: string[];
  filteredCrmFields: CrmField[];
  requiredFields: CampaignField[];
  optionalFields: CampaignField[];
}

const FieldMappingContext = createContext<FieldMappingContextValue | undefined>(
  undefined,
);

export const useFieldMapping = () => {
  const context = useContext(FieldMappingContext);
  if (context === undefined) {
    throw new Error(
      'useFieldMapping must be used within a FieldMappingProvider',
    );
  }
  return context;
};

interface FieldMappingProviderProps {
  children: ReactNode;
}

export const FieldMappingProvider: React.FC<FieldMappingProviderProps> = ({
  children,
}) => {
  const authedRequest = useAuthedRequest();
  const integrationApp = useIntegrationApp();
  const { integratedCrm, segmentId, currentStep } = useCrmSync();
  const { campaign } = useCampaignContext();

  // State
  const [availableCrmFields, setAvailableCrmFields] = useState<CrmField[]>([]);
  const [campaignFields, setCampaignFields] = useState<CampaignField[]>([]);
  const [mappingEntries, setMappingEntries] = useState<FieldMappingEntry[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sampleContacts, setSampleContacts] = useState<ContactRecord[]>([]);

  // Internal function to fetch contacts for field mapping
  const fetchContactsFromCrm = useCallback(
    async (
      integratedCrm: Option | null,
      segmentId?: string,
      maxContacts?: number,
    ): Promise<ContactRecord[]> => {
      const response = await listAction<ContactRecord>({
        client: integrationApp,
        integrationKey: integratedCrm?.value || null,
        dataSourceKey: DataSourceKey.CONTACTS,
        segmentId,
        maxRecords: maxContacts,
      });

      return response.records;
    },
    [integrationApp],
  );

  // Load campaign fields
  useEffect(() => {
    const loadCampaignFields = async () => {
      if (!campaign || currentStep === CrmSyncStep.configuration) return;

      try {
        const fields: CampaignField[] = [];

        // Always required: phone number
        fields.push({
          name: 'to_number',
          label: 'To number',
          required: true,
          description: 'Phone number for calls and SMS',
        });

        // Required if email campaign is configured
        if (campaign.emailCampaignConfig) {
          fields.push({
            name: 'to_email',
            label: 'To Email',
            required: true,
            description: 'Email address for email communications',
          });
        }

        // Validate campaign structure before proceeding
        if (
          !campaign.initialCallConfig ||
          !campaign.followUpCommunicationConfigs
        ) {
          console.error('Campaign missing required properties:', {
            hasInitialCallConfig: !!campaign.initialCallConfig,
            hasFollowUpCommunicationConfigs:
              !!campaign.followUpCommunicationConfigs,
          });
          throw new Error('Campaign object is missing required properties');
        }

        // Extract context parameters from agents and campaign
        const agentIds = getCampaignAgentIds(campaign);
        const agents = await fetchAgentsById(agentIds, authedRequest);

        let contextParams: string[] = [];

        agents.forEach((agent) => {
          const referencedContextParams = getReferencedContextParams(agent);
          contextParams.push(...referencedContextParams);
        });

        const campaignContextParams = getCampaignContextParams(campaign);
        contextParams = Array.from(
          new Set([...contextParams, ...campaignContextParams]),
        );

        // Add context parameters as optional fields
        contextParams.sort().forEach((param) => {
          fields.push({
            name: `context.${param}`,
            label: param,
            required: false,
            description: 'Optional context parameter',
          });
        });

        setCampaignFields(fields);

        // Initialize field mappings
        const initialMappings = fields.map((field) => ({
          campaignField: field.name,
          crmFieldValues: [],
        }));
        setMappingEntries((prevMappings) => {
          if (prevMappings.length === 0) {
            return initialMappings;
          }
          return initialMappings.map((mapping) => {
            const existing = prevMappings.find(
              (prevMapping) =>
                prevMapping.campaignField === mapping.campaignField,
            );
            return {
              ...mapping,
              crmFieldValues: existing?.crmFieldValues ?? [],
            };
          });
        });
      } catch (error) {
        console.error('Failed to load campaign fields:', error);
      }
    };

    loadCampaignFields();
  }, [campaign, currentStep, authedRequest]);

  // Load available CRM fields from sample contacts
  useEffect(() => {
    const extractFieldsFromContacts = () => {
      if (
        !integratedCrm ||
        !sampleContacts.length ||
        currentStep === CrmSyncStep.configuration
      )
        return;

      try {
        const fieldsSet = new Set<string>();
        const fields: CrmField[] = [];

        // Extract all unique field names from sample contacts
        sampleContacts.forEach((contact) => {
          Object.keys(contact.fields).forEach((fieldName) => {
            fieldsSet.add(fieldName);
          });
        });

        // Convert to CrmField objects
        Array.from(fieldsSet).forEach((fieldName) => {
          fields.push({
            value: fieldName,
            label: fieldName,
            description: `Field from ${integratedCrm.label}`,
          });
        });

        // Sort fields alphabetically by label for better UX
        // Fields with object values (stringified as [object Object]) go to the end
        fields.sort((a, b) => {
          // Check if field values are objects in the first sample contact
          let isAObject = false;
          let isBObject = false;

          if (sampleContacts.length > 0) {
            const firstContact = sampleContacts[0];
            const fieldValueA =
              firstContact.fields[a.value as keyof typeof firstContact.fields];
            const fieldValueB =
              firstContact.fields[b.value as keyof typeof firstContact.fields];

            if (fieldValueA && String(fieldValueA) === '[object Object]') {
              isAObject = true;
            }
            if (fieldValueB && String(fieldValueB) === '[object Object]') {
              isBObject = true;
            }
          }

          // If one is an object and the other isn't, object goes to the end
          if (isAObject && !isBObject) return 1;
          if (!isAObject && isBObject) return -1;

          // If both are objects or both aren't, sort normally by label
          return a.label.localeCompare(b.label);
        });

        setAvailableCrmFields(fields);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to extract CRM fields from contacts:', error);
        setIsInitialized(true);
      }
    };

    extractFieldsFromContacts();
  }, [integratedCrm, sampleContacts, currentStep]);

  // Load sample contact data for preview
  useEffect(() => {
    const loadSampleContacts = async () => {
      if (!integratedCrm || currentStep === CrmSyncStep.configuration) return;

      try {
        const contacts = await fetchContactsFromCrm(
          integratedCrm,
          segmentId,
          5,
        );
        setSampleContacts(contacts);
      } catch (error) {
        console.error('Failed to load sample contacts:', error);
        setSampleContacts([]);
      }
    };

    if (integratedCrm) {
      loadSampleContacts();
    }
  }, [integratedCrm, segmentId, currentStep, fetchContactsFromCrm]);

  // Generate expression from CRM fields array
  const generateExpression = useCallback((crmFieldValues: string[]): string => {
    if (crmFieldValues.length === 0) {
      return '';
    }
    if (crmFieldValues.length === 1) {
      return `{{event.fields.${crmFieldValues[0]}}}`;
    }
    const values = crmFieldValues.map((v) => `event.fields.${v}`).join(',');
    return `{{firstNonEmpty({"values":[${values}]})}}`;
  }, []);

  // Add a CRM field to a campaign field mapping
  const addFieldToMapping = useCallback(
    (
      campaignField: string,
      crmFieldValue: string,
      options?: AddFieldToMappingOptions,
    ) => {
      setMappingEntries((prevMappings) => {
        const updatedMappings = prevMappings.map((mapping) => {
          if (mapping.campaignField !== campaignField) {
            return mapping;
          }
          // Don't add if already exists
          if (mapping.crmFieldValues.includes(crmFieldValue)) {
            return mapping;
          }
          const requestedIndex = options?.index;
          const insertAt =
            typeof requestedIndex === 'number'
              ? Math.max(
                  0,
                  Math.min(requestedIndex, mapping.crmFieldValues.length),
                )
              : mapping.crmFieldValues.length;
          const nextFieldValues = [...mapping.crmFieldValues];
          nextFieldValues.splice(insertAt, 0, crmFieldValue);
          return {
            ...mapping,
            crmFieldValues: nextFieldValues,
          };
        });
        return updatedMappings;
      });
    },
    [],
  );

  // Remove a CRM field from a campaign field mapping
  const removeFieldFromMapping = useCallback(
    (campaignField: string, crmFieldValue: string) => {
      setMappingEntries((prevMappings) => {
        const updatedMappings = prevMappings.map((mapping) => {
          if (mapping.campaignField !== campaignField) {
            return mapping;
          }
          return {
            ...mapping,
            crmFieldValues: mapping.crmFieldValues.filter(
              (value) => value !== crmFieldValue,
            ),
          };
        });
        return updatedMappings;
      });
    },
    [],
  );

  // Reorder CRM fields within a campaign field mapping
  const reorderFieldsInMapping = useCallback(
    (campaignField: string, fromIndex: number, toIndex: number) => {
      setMappingEntries((prevMappings) => {
        const updatedMappings = prevMappings.map((mapping) => {
          if (mapping.campaignField !== campaignField) {
            return mapping;
          }
          const newFieldValues = [...mapping.crmFieldValues];
          const [movedField] = newFieldValues.splice(fromIndex, 1);
          newFieldValues.splice(toIndex, 0, movedField);
          return {
            ...mapping,
            crmFieldValues: newFieldValues,
          };
        });
        return updatedMappings;
      });
    },
    [],
  );

  // Clear all CRM fields from a campaign field mapping
  const clearMapping = useCallback((campaignField: string) => {
    setMappingEntries((prevMappings) => {
      const updatedMappings = prevMappings.map((mapping) => {
        if (mapping.campaignField !== campaignField) {
          return mapping;
        }
        return {
          ...mapping,
          crmFieldValues: [],
        };
      });
      return updatedMappings;
    });
  }, []);

  const fieldMapping = useMemo(() => {
    const mappingObject: Record<string, string> = {};
    mappingEntries.forEach((mapping) => {
      const expression = generateExpression(mapping.crmFieldValues);
      if (expression) {
        mappingObject[mapping.campaignField] = expression;
      }
    });
    return mappingObject;
  }, [generateExpression, mappingEntries]);

  // Computed values - flatten all mapped CRM field values
  const mappedFieldValues = mappingEntries.flatMap(
    (mapping) => mapping.crmFieldValues,
  );

  const filteredCrmFields = availableCrmFields.filter(
    (field) =>
      !mappedFieldValues.includes(field.value) &&
      (field.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.value.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const requiredFields = campaignFields.filter((field) => field.required);
  const optionalFields = campaignFields.filter((field) => !field.required);

  const isFieldMappingValid = useMemo(() => {
    const requiredCampaignFields = campaignFields.filter(
      (field) => field.required,
    );
    const mappedRequiredFieldsCount = mappingEntries.filter(
      (mapping) =>
        requiredCampaignFields.some(
          (field) => field.name === mapping.campaignField,
        ) && mapping.crmFieldValues.length > 0,
    ).length;
    return mappedRequiredFieldsCount === requiredCampaignFields.length;
  }, [campaignFields, mappingEntries]);

  const contextValue = {
    // State
    fieldMapping,
    isFieldMappingValid,
    availableCrmFields,
    campaignFields,
    mappingEntries,
    isInitialized,
    searchQuery,
    sampleContacts,

    // Computed values
    mappedFieldValues,
    filteredCrmFields,
    requiredFields,
    optionalFields,

    // Setters
    setSearchQuery,

    // Actions
    addFieldToMapping,
    removeFieldFromMapping,
    reorderFieldsInMapping,
    clearMapping,
  };

  return (
    <FieldMappingContext.Provider value={contextValue}>
      {children}
    </FieldMappingContext.Provider>
  );
};
